import PlatformAdminPanel from '../../../../apps/timely/PlatformAdminPanel';
import DateTimePreferences from '../../../util/DateTimePreferences';
import { useLocation } from 'react-router-dom';
import TimezoneSelector from '../../../util/timezoneSelector';
import { useTimeZones } from '../../../util/timezones';
import { Button, Alert, MenuItem } from '@mui/material';
import React, { useState, useRef, useContext, useCallback, useEffect, useMemo } from "react";
import { Container, Grid, Paper, Tabs, Tab, Typography, Box, TextField } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import UserPageGeneral from "./userPageGeneral";
import UserPageChangepassword from "./userPageChangepassword";
import { useIsMobile } from "../../../util/utils";
import { UserContext } from "../../../components/auth/auth-context";
import { setSelectedBusiness } from "../../../redux/actions/businessActions";
import { useSelectBusinesses, useSelectedBusiness } from "../../../redux/store";
import useLoadReduxStoreData from "../../../redux/reducers/loadReduxStoreData";
import { BusinessQueryNames } from "../../../graphql/queries";
import { UPDATE_DEFAULT_BUSINESS, UPDATE_PROFILE } from "../../../graphql/mutations";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import DefaultPageDialog, {
  getPageOptions,
  isAFHCategoryName,
  resolveDefaultPageIdForBusiness,
} from "../defaultPageDialog";
import jwtDecode from "jwt-decode";
import { GET_BUSINESS_CATEGORIES, GET_TOKEN } from "../../../graphql/queries";
import { GET_USER_PROFILE } from "../../../graphql/queries/user";
import { setToken } from "../../../util/authUtils";
import { toFiniteIntOrNull } from "../../../util/timezoneUtils";
import { hasStandaloneBookingAccess } from "../../../../core/business/productAccess";
import { SMS_CONSENT_TEXT } from "../../../components/legal/SmsConsentCheckbox";

const headerTitles = ["General Profile", "Change Your Password", "Default Start Page", "Regional preferences", "Platform administration"];

const UserPage = () => {
  const regionalLocation = useLocation();
  useEffect(() => { if (new URLSearchParams(regionalLocation.search).get('section') === 'regional') setTabValue(3); else if (new URLSearchParams(regionalLocation.search).get('section') === 'admin') setTabValue(4); }, [regionalLocation.search, regionalLocation.key]);
  const getSmsOptInFromUser = (user) => {
    if (!user || typeof user !== "object") return null;
    const candidates = [
      user.smsOptIn,
      user.smsoptin,
      user.sms_opt_in,
      user.smsConsent,
    ];
    const found = candidates.find((v) => typeof v === "boolean");
    return typeof found === "boolean" ? found : null;
  };

  const toLocalUsPhone = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (!digits) return "";
    if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
    return String(value || '').startsWith('+') ? '+' + digits : digits;
  };

  const toE164UsPhone = (value, selectedCountry) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (!digits) return null;
    if (String(value || '').startsWith('+') && digits.length >= 7 && digits.length <= 15) return '+' + digits;
    if (selectedCountry === 'Ethiopia' && /^0?\d{9}$/.test(digits)) return '+251' + digits.replace(/^0/, '');
    const local = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    if (local.length !== 10) return null;
    return `+1${local}`;
  };

  const dispatch = useDispatch();
  const apolloClient = useApolloClient();
  const [tabValue, setTabValue] = useState(() => new URLSearchParams(window.location.search).get('section') === 'regional' ? 3 : new URLSearchParams(window.location.search).get('section') === 'admin' ? 4 : 0);
  const isMobile = useIsMobile();
  const { providerUser: token, updateProviderUser } = useContext(UserContext);
  const { timeZones } = useTimeZones();
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [timezoneError, setTimezoneError] = useState('');
  const phoneStorageKey = token?.userId ? `profilePhone:${token.userId}` : "profilePhone:default";
  const smsOptInStorageKey = token?.userId ? `profileSmsOptIn:${token.userId}` : "profileSmsOptIn:default";

  // Load businesses
  useLoadReduxStoreData(BusinessQueryNames.GET_MY_BUSINESSES);
  const businesses = useSelectBusinesses();
  const selectedBusiness = useSelectedBusiness();
  const { data: categoriesData } = useQuery(GET_BUSINESS_CATEGORIES);
  const { data: userProfileData } = useQuery(GET_USER_PROFILE, {
    variables: { id: Number(token?.userId || 0) },
    skip: !Number(token?.userId || 0),
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  // Profile mutation for general form
  const [update] = useMutation(UPDATE_PROFILE, {
    refetchQueries: ['GetUserProfile', 'CalendarPreferences'],
    awaitRefetchQueries: true,
  });
  const [updateDefaultBusiness] = useMutation(UPDATE_DEFAULT_BUSINESS);

  // Existing profile state
  const [username, setUsername] = useState(token.username);
  const [email] = useState(token.email);
  const [phone, setPhone] = useState(() => {
    const fromToken = toLocalUsPhone(token.phone);
    if (fromToken) return fromToken;
    try {
      return toLocalUsPhone(localStorage.getItem(phoneStorageKey));
    } catch (_err) {
      return "";
    }
  });
  const [address, setAddress] = useState(token.address || "");
  const [city, setCity] = useState(token.city || "");
  const [state, setState] = useState(token.state || "");
  const [country, setCountry] = useState(token.country || "");
  const [zipcode, setZipcode] = useState(token.zipcode || "");
  const [desc, setDesc] = useState(token.desc);
  const [smsOptIn, setSmsOptIn] = useState(() => {
    const fromToken = getSmsOptInFromUser(token);
    if (typeof fromToken === "boolean") return fromToken;
    try {
      const raw = localStorage.getItem(smsOptInStorageKey);
      return raw === "true";
    } catch (_err) {
      return false;
    }
  });
  const [smsConsentChanged, setSmsConsentChanged] = useState(false);
  const [defaultBusinessId, setDefaultBusinessId] = useState(token.defaultBusinessId);
  const [selectedTimeZoneId, setSelectedTimeZoneId] = useState(token.timeZone?.id);
  const [generalUpdated, setGeneralUpdated] = useState(false);
  const [errors, setErrors] = useState("");

  // Track defaultPageId and derive label
  const [defaultPageId, setDefaultPageId] = useState(
    token.defaultPageId != null ? token.defaultPageId : 1
  );
  const selectedDefaultBusiness = useMemo(
    () => businesses.find((b) => Number(b?.businessBasic?.id) === Number(defaultBusinessId)),
    [businesses, defaultBusinessId]
  );

  useEffect(() => {
    const selectedBusinessId = Number(selectedBusiness?.businessBasic?.id || 0);
    if (selectedBusinessId > 0 && selectedBusinessId !== Number(defaultBusinessId || 0)) {
      setDefaultBusinessId(selectedBusinessId);
    }
  }, [selectedBusiness?.businessBasic?.id, defaultBusinessId]);

  useEffect(() => {
    if (!Array.isArray(businesses) || businesses.length === 0) return;

    const currentDefaultId = Number(defaultBusinessId || 0);
    const currentExists = currentDefaultId
      ? businesses.some(
          (business) => Number(business?.businessBasic?.id) === currentDefaultId
        )
      : false;
    if (currentExists) return;

    const tokenDefaultId = Number(token?.defaultBusinessId || 0);
    const tokenExists = tokenDefaultId
      ? businesses.some(
          (business) => Number(business?.businessBasic?.id) === tokenDefaultId
        )
      : false;

    const fallbackBusinessId = tokenExists
      ? tokenDefaultId
      : Number(businesses[0]?.businessBasic?.id || 0);

    if (fallbackBusinessId > 0) {
      setDefaultBusinessId(fallbackBusinessId);
      dispatch(setSelectedBusiness(fallbackBusinessId));
    }
  }, [businesses, defaultBusinessId, dispatch, token?.defaultBusinessId]);
  const selectedCategory = useMemo(
    () =>
      (categoriesData?.getBusinessCategories || []).find(
        (c) =>
          Number(c?.id) === Number(selectedDefaultBusiness?.businessBasic?.businessCategoryId)
      ),
    [categoriesData?.getBusinessCategories, selectedDefaultBusiness?.businessBasic?.businessCategoryId]
  );
  const isAFHBusiness = isAFHCategoryName(selectedCategory?.name);
  const canUseStandaloneBooking = useMemo(
    () => hasStandaloneBookingAccess(token, isAFHBusiness),
    [token, isAFHBusiness]
  );
  const allowedPageOptions = useMemo(
    () => getPageOptions(isAFHBusiness, canUseStandaloneBooking),
    [isAFHBusiness, canUseStandaloneBooking]
  );
  const effectiveDefaultPageId = useMemo(
    () =>
      resolveDefaultPageIdForBusiness(
        defaultPageId,
        isAFHBusiness,
        canUseStandaloneBooking
      ),
    [defaultPageId, isAFHBusiness, canUseStandaloneBooking]
  );
  const hasBusinessProfiles = useMemo(
    () =>
      Array.isArray(businesses) &&
      businesses.some((b) => Number(b?.businessBasic?.id || 0) > 0),
    [businesses]
  );
  const defaultPageLabel =
    allowedPageOptions.find((opt) => opt.id === effectiveDefaultPageId)?.label || "";

  useEffect(() => {
    if (!hasBusinessProfiles && tabValue === 2) {
      setTabValue(0);
    }
  }, [hasBusinessProfiles, tabValue]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const openDialog = () => setDialogOpen(true);
  const closeDialog = () => setDialogOpen(false);

  // Parent callback when dialog saves
  const handleDialogSaved = (pageId) => {
    setDefaultPageId(pageId);
  };

  // Default business change handler
  const handleSelectDefaultBusinessChange = async (e) => {
    const selectedBusinessId = Number(e.target.value);
    const selectedBusiness = businesses.find(
      (business) => Number(business.businessBasic.id) === Number(selectedBusinessId)
    );
    if (selectedBusiness) {
      try {
        setDefaultBusinessId(selectedBusiness.businessBasic.id);
        await updateDefaultBusiness({
          variables: { defaultBusinessId: Number(selectedBusiness.businessBasic.id) },
        });
        const refreshedTokenResult = await apolloClient.query({
          query: GET_TOKEN,
          variables: {
            userId: Number(token.userId),
            businessId: Number(selectedBusiness.businessBasic.id),
            staffId: null,
            subscriptionId: null,
          },
          fetchPolicy: "network-only",
        });

        const refreshedToken = refreshedTokenResult?.data?.getToken?.token;
        if (refreshedToken) {
          setToken(refreshedToken);
          const decoded = jwtDecode(refreshedToken);
          updateProviderUser(decoded);
          if (decoded?.defaultBusinessId) {
            setDefaultBusinessId(decoded.defaultBusinessId);
            dispatch(setSelectedBusiness(decoded.defaultBusinessId));
          }
        } else {
          updateProviderUser({
            defaultBusinessId: Number(selectedBusiness.businessBasic.id),
          });
        }

        setGeneralUpdated(false);
        dispatch(setSelectedBusiness(selectedBusinessId));
      } catch (error) {
        console.error("Failed to update default business:", error);
      }
    }
  };

  useEffect(() => {
    const fromToken = toLocalUsPhone(token.phone);
    if (fromToken) {
      setPhone(fromToken);
      try {
        localStorage.setItem(phoneStorageKey, fromToken);
      } catch (_err) {
        // ignore storage errors
      }
    }
  }, [token.phone, phoneStorageKey]);

  useEffect(() => {
    if (generalUpdated) return;

    const profile = userProfileData?.user || {};

    setUsername(profile.username || token.username || "");
    setAddress(profile.address || token.address || "");
    setCity(profile.city || token.city || "");
    setState(profile.state || token.state || "");
    setCountry(profile.country || token.country || "");
    setZipcode(profile.zipcode || token.zipcode || "");
    setDesc(profile.desc || token.desc || "");

    const profilePhone = toLocalUsPhone(profile.phone);
    if (profilePhone) {
      setPhone(profilePhone);
      try {
        localStorage.setItem(phoneStorageKey, profilePhone);
      } catch (_err) {
        // ignore storage errors
      }
    }

    const nextDefaultBusinessId = profile.defaultBusinessId || token.defaultBusinessId || "";
    setDefaultBusinessId(nextDefaultBusinessId);

    const nextTimeZoneId = profile.timeZone?.id ?? token.timeZone?.id;
    setSelectedTimeZoneId(nextTimeZoneId);

    if (profile?.id && typeof updateProviderUser === "function") {
      updateProviderUser({
        username: profile.username || token.username || "",
        phone: profile.phone || token.phone || "",
        address: profile.address || token.address || "",
        city: profile.city || token.city || "",
        state: profile.state || token.state || "",
        country: profile.country || token.country || "",
        zipcode: profile.zipcode || token.zipcode || "",
        desc: profile.desc || token.desc || "",
        defaultBusinessId: nextDefaultBusinessId || token.defaultBusinessId,
        defaultPageId: profile.defaultPageId ?? token.defaultPageId,
        timeZone: profile.timeZone || token.timeZone,
      });
    }
  }, [
    userProfileData,
    token.username,
    token.address,
    token.city,
    token.state,
    token.country,
    token.zipcode,
    token.desc,
    token.defaultBusinessId,
    token.defaultPageId,
    token.timeZone?.id,
    token.phone,
    phoneStorageKey,
    updateProviderUser,
    generalUpdated,
  ]);

  useEffect(() => {
    const fromToken = getSmsOptInFromUser(token);
    if (typeof fromToken === "boolean") {
      setSmsOptIn(fromToken);
      setSmsConsentChanged(false);
      try {
        localStorage.setItem(smsOptInStorageKey, String(fromToken));
      } catch (_err) {
        // ignore storage errors
      }
      return;
    }
    try {
      const raw = localStorage.getItem(smsOptInStorageKey);
      if (raw === "true" || raw === "false") {
        setSmsOptIn(raw === "true");
      }
    } catch (_err) {
      // ignore storage errors
    }
  }, [token, smsOptInStorageKey]);

  useEffect(() => {
    if (phone) return;
    const allStaffs = (businesses || []).flatMap((b) => b?.businessStaffs || []);
    const myStaff = allStaffs.find((s) => Number(s?.user?.id) === Number(token?.userId));
    const fromBusinessUser = toLocalUsPhone(myStaff?.user?.phone);
    if (fromBusinessUser) {
      setPhone(fromBusinessUser);
      try {
        localStorage.setItem(phoneStorageKey, fromBusinessUser);
      } catch (_err) {
        // ignore storage errors
      }
    }
  }, [businesses, phone, token?.userId, phoneStorageKey]);

  // General form submit (other fields)
  const handleSubmit = async (overrides = {}) => {
    try {
      const safeOverrides =
        overrides &&
        Object.prototype.toString.call(overrides) === "[object Object]"
          ? overrides
          : {};

      const parsedTimeZoneId = toFiniteIntOrNull(selectedTimeZoneId);
      const variables = {
        userId: Number(token.userId),
        email,
        username,
        phone: toE164UsPhone(phone, country),
        address,
        city,
        state,
        country,
        zipcode,
        ...(smsConsentChanged
          ? {
            smsOptIn,
            smsConsentText: SMS_CONSENT_TEXT,
          }
          : {}),
        hours24: token.hours24,
        ...(parsedTimeZoneId != null ? { timeZoneId: parsedTimeZoneId } : {}),
        desc,
        calendar: token.calendar,
        defaultBusinessId,
        defaultPageId: effectiveDefaultPageId,
        ...safeOverrides,
      };

      const res = await update({ variables });
      setErrors("");
      setGeneralUpdated(false);
      setSmsConsentChanged(false);
      try {
        localStorage.setItem(phoneStorageKey, phone);
        localStorage.setItem(smsOptInStorageKey, String(smsOptIn));
      } catch (_err) {
        // ignore storage errors
      }
      const newToken = res?.data?.updateProfile?.token;
      if (newToken) {
        setToken(newToken);
        try {
          const decoded = jwtDecode(newToken);
          updateProviderUser(decoded);
          if (decoded?.defaultBusinessId) {
            setDefaultBusinessId(decoded.defaultBusinessId);
            dispatch(setSelectedBusiness(decoded.defaultBusinessId));
          }
        } catch (err) {
          console.error("Failed to decode updated token:", err);
        }
      }
    } catch (err) {
      console.error(err);
      setErrors(err?.message || "Could not save profile changes.");
      setGeneralUpdated(true);
    }
  };

  const handleTimezoneSelect = useCallback(
    (tz) => {
      if (tz.timezone !== token.timeZone?.timezone) {
        setSelectedTimeZoneId(tz.id);
        setGeneralUpdated(true);
      }
    },
    [token.timeZone]
  );

  const useEthiopiaTimezone = async () => {
    setTimezoneError('');
    const timezone = timeZones.find(zone => zone.timezone === 'Africa/Addis_Ababa');
    if (!timezone) { setTimezoneError('Could not load Addis Ababa timezone. Refresh and try again.'); return; }
    setSavingTimezone(true);
    try {
      const result = await update({ variables: { userId: Number(token.userId), timeZoneId: Number(timezone.id) } });
      const nextToken = result?.data?.updateProfile?.token;
      if (!nextToken) throw Error('Could not confirm the saved timezone. Please refresh.');
      setToken(nextToken);
      updateProviderUser(jwtDecode(nextToken));
      setSelectedTimeZoneId(timezone.id);
    } catch (error) { setTimezoneError(error.message || 'Could not save timezone. Try again.'); }
    finally { setSavingTimezone(false); }
  };

  const notificationRef = useRef();
  const handleTabChange = (_, newValue) => setTabValue(newValue);

  return (
    <>
      <Box>
        <Typography
          variant="h5"
          sx={{ fontSize: { xs: "1.5rem", sm: "2rem" }, textAlign: "center" }}
        >
          {headerTitles[tabValue]}
        </Typography>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 8 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={isMobile ? 12 : 3}>
            <Paper>
              <Tabs
                variant="scrollable"
                scrollButtons="auto"
                orientation={isMobile ? "horizontal" : "vertical"}
                value={tabValue}
                onChange={handleTabChange}
              >
                <Tab value={0} label="General" />
                <Tab value={3} label="Regional preferences" />
                {token?.isPlatformAdmin && <Tab value={4} label="Platform administration" />}
                <Tab value={1} label="Change Password" />
                {hasBusinessProfiles ? <Tab value={2} label=" Set Default Start Page" /> : null}
              </Tabs>

            </Paper>

          </Grid>

          <Grid item xs={12} sm={isMobile ? 12 : 9}>
            <Paper>
              {tabValue === 4 && (token?.isPlatformAdmin ? <PlatformAdminPanel /> : <Alert severity="error">Platform administrator access required.</Alert>)}
              {tabValue === 3 && <Box p={{ xs: 2, sm: 3 }}>
                <Typography variant="h6">Your dates and times</Typography>
                <Typography color="text.secondary" variant="body2">Choose how dates and times appear throughout Timely. Existing bookings and recurrence rules stay the same.</Typography>
                <DateTimePreferences timezone={token.timeZone?.timezone} onUseEthiopia={useEthiopiaTimezone} savingTimezone={savingTimezone} timezoneError={timezoneError} onAdjustTimezone={() => { const field=document.getElementById('regional-timezone'); field?.scrollIntoView({behavior:'smooth',block:'center'}); field?.querySelector('input')?.focus(); }} />
                <TextField select fullWidth size="small" label="Language" value="en" disabled helperText="English is currently available. Your calendar choice does not change the language." sx={{ mb: 3 }}><MenuItem value="en">English</MenuItem></TextField>
                <Box id="regional-timezone"><TimezoneSelector isDisabled={savingTimezone} timezoneId={selectedTimeZoneId ?? token.timeZone?.id} onTimezoneSelect={handleTimezoneSelect} /></Box>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>Your calendar, appointment display, and current-time indicator follow this timezone. Choose Africa/Addis_Ababa for Ethiopia's local time. Existing schedules keep their own timezone.</Typography>
                <Button sx={{ mt: 2 }} variant="contained" disabled={savingTimezone || String(selectedTimeZoneId) === String(token.timeZone?.id)} onClick={() => handleSubmit()}>Save timezone</Button>
                {errors && <Alert severity="error" sx={{ mt: 2 }}>{errors}</Alert>}
              </Box>}
              {tabValue === 0 && (
                <Box p={2}>

                  <UserPageGeneral
                    defaultBusinessId={defaultBusinessId}
                    defaultBusinessLoading={false}
                    defaultBusinessError={null}
                    businesses={businesses}
                    handleSelectDefaultBusinessChange={
                      handleSelectDefaultBusinessChange
                    }
                    username={username}
                    setUsername={setUsername}
                    setUpdated={setGeneralUpdated}
                    email={email}
                    phone={phone}
                    setPhone={setPhone}
                    address={address}
                    setAddress={setAddress}
                    city={city}
                    setCity={setCity}
                    state={state}
                    setState={setState}
                    country={country}
                    setCountry={setCountry}
                    zipcode={zipcode}
                    setZipcode={setZipcode}
                    smsOptIn={smsOptIn}
                    setSmsOptIn={setSmsOptIn}
                    setSmsConsentChanged={setSmsConsentChanged}
                    selectedTimeZoneId={selectedTimeZoneId}
                    token={token}
                    handleTimezoneSelect={handleTimezoneSelect}
                    handleSubmit={handleSubmit}
                    updated={generalUpdated}
                    notificationRef={notificationRef}
                    desc={desc}
                    setDesc={setDesc}
                    errors={errors}
                  />
                </Box>
              )}

              {tabValue === 1 && (
                <UserPageChangepassword error={false} setError={() => { }} />
              )}

              {hasBusinessProfiles && tabValue === 2 && (
                <Box p={2}>
                  {defaultBusinessId && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Default start page
                      </Typography>
                      <TextField
                        fullWidth
                        label="Select a page"
                        value={defaultPageLabel || ""}
                        placeholder="Select a page"
                        onClick={openDialog}
                        InputProps={{
                          readOnly: true,
                          endAdornment: <ArrowDropDownIcon />,
                        }}
                        sx={{ mb: 1.5 }}
                      />

                      <Typography
                        variant="body2"
                        sx={{ textAlign: "center", color: "text.secondary" }}
                      >
                        This page will open automatically every time you log in — until you change it.
                      </Typography>
                    </>
                  )}
                </Box>
              )}


            </Paper>
          </Grid>
        </Grid>
      </Container>
      <DefaultPageDialog
        open={dialogOpen}
        value={effectiveDefaultPageId}
        isAFHBusiness={isAFHBusiness}
        canUseStandaloneBooking={canUseStandaloneBooking}
        onClose={closeDialog}
        onSaved={handleDialogSaved}
      />
    </>
  );
};

export default UserPage;
